import { type Dispatch, type FC, type SetStateAction } from 'react';
import cn from 'classnames';
import { motion } from 'framer-motion';

import { playSound } from '@/common/lib/soundEffect';
import { hapticFeedbackStyle } from '@/components/helpers/hapticFeedbackFunction';
import { Button } from '@/components/ui/Button/Button';

interface ProducePercentageProps {
  percentage: number;
  setPercentage: Dispatch<SetStateAction<number>>;
  onClick: (percentage: number) => void;
}

const ProducePercentage: FC<ProducePercentageProps> = ({
  percentage,
  setPercentage,
  onClick,
}) => {
  const changePercentage = (percentage: number): void => {
    setPercentage(percentage);
    playSound('plants/percentage-selection');

    if (onClick) {
      onClick(percentage);
    }
    hapticFeedbackStyle('light');
  };

  return (
    <div className="w-full flex gap-1 h-10 bg-white/10 rounded-lg">
      {Array.from({ length: 4 }).map((_, index) => {
        const label = index * 25 + 25;
        const isActive = percentage === label;

        return (
          <Button
            key={`value-${label}`}
            variant="transparent"
            onClick={() => changePercentage(label)}
            className="h-full"
          >
            <div
              className={cn(
                'relative h-full flex-1 rounded-lg',
                isActive && 'bg-white/10',
              )}
            >
              <motion.span
                key={`animate-${isActive}`}
                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 leading-none font-bold"
                initial={{
                  color: '#FFFFFF99',
                  fontSize: '0.75rem',
                }}
                animate={
                  isActive
                    ? { color: '#FFFFFF', fontSize: '1rem' }
                    : {
                        color: '#FFFFFF99',
                        fontSize: '0.75rem',
                      }
                }
              >
                {label}%
              </motion.span>
            </div>
          </Button>
        );
      })}
    </div>
  );
};

export { ProducePercentage };
